import { AlarmClock, Clock, Eye, MapPin, UserX } from 'lucide-react';
import { SECTOR_OPTIONS, EMPLOYEE_TIER_OPTIONS, SOURCE_OPTIONS } from '../config/leadTaxonomy';
import { isTerminalStage } from '../services/leadService';
import type { Lead, LeadTask } from '../types/lead.types';
import { formatDate, isDueToday, isOverdue } from '../utils/date';

interface Props {
  lead: Lead;
  /** Task aperto più vicino a scadere per questo lead, se presente — v.
   * subscribeToOpenTasks in leadService.ts (un solo listener condiviso da
   * tutte le card, non uno a lead). */
  nextTask?: LeadTask;
  onClick: () => void;
}

export function LeadCard({ lead, nextTask, onClick }: Props) {
  const sectorLabel = SECTOR_OPTIONS.find((s) => s.id === lead.sector)?.label ?? lead.sector;
  const sourceLabel = SOURCE_OPTIONS.find((s) => s.id === lead.source)?.label ?? lead.source;
  const tierLabel = lead.employeeTier ? EMPLOYEE_TIER_OPTIONS.find((t) => t.id === lead.employeeTier)?.label : null;
  const followUp = formatDate(lead.nextFollowUpAt);

  // Un lead vinto/perso non si trascina più — è uno stato finale raggiungibile
  // solo dai flussi dedicati (Segna Vinto/Perso nel dettaglio), mai da un drop.
  const draggable = !isTerminalStage(lead.stage);

  // Il badge task compare solo per le scadenze "azionabili" (in ritardo o
  // oggi) — mostrare ogni task futuro su ogni card sarebbe rumore, non segnale.
  const overdue = nextTask ? isOverdue(nextTask.dueAt) : false;
  const dueToday = nextTask ? isDueToday(nextTask.dueAt) : false;

  return (
    <button
      draggable={draggable}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', lead.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onClick}
      className="w-full text-left bg-white rounded-xl border border-gray-200 p-3 shadow-sm hover:shadow-md transition-shadow cursor-grab active:cursor-grabbing"
    >
      <p className="font-semibold text-sm text-navy-900 truncate">{lead.organizationName}</p>
      {lead.contactName ? (
        <p className="text-xs text-gray-500 truncate">
          {lead.contactName}
          {lead.contactSource === 'ai_web_search' && (
            <span className="ml-1 text-[9px] uppercase font-semibold text-indigo-500" title="Trovato dall'AI sul sito aziendale — verificare prima di usarlo">
              AI · da verificare
            </span>
          )}
        </p>
      ) : (
        <p className="flex items-center gap-1 text-xs text-amber-600 truncate">
          <UserX size={11} /> Contatto da individuare
        </p>
      )}
      {lead.city && (
        <p className="flex items-center gap-1 text-xs text-gray-500 truncate mt-0.5">
          <MapPin size={11} className="shrink-0" />
          {lead.streetAddress ? `${lead.streetAddress}, ` : ''}{lead.postalCode} {lead.city}
        </p>
      )}
      <div className="flex flex-wrap gap-1 mt-2">
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{sectorLabel}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">{sourceLabel}</span>
        {tierLabel && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">{tierLabel}</span>}
        {lead.dataProfilingLikely && (
          <span
            title={lead.dataProfilingReasoning}
            className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700"
          >
            <Eye size={9} /> Probabile profilazione
          </span>
        )}
      </div>
      {lead.estimatedDealValue != null && (
        <p className="text-xs font-medium text-swiss-red mt-2">CHF {lead.estimatedDealValue.toLocaleString('it-CH')}</p>
      )}
      {followUp && (
        <p className="flex items-center gap-1 text-[10px] text-gray-400 mt-1">
          <Clock size={10} /> {followUp}
        </p>
      )}
      {nextTask && (overdue || dueToday) && (
        <p className={`flex items-center gap-1 text-[10px] font-medium mt-1 truncate ${overdue ? 'text-swiss-red' : 'text-amber-600'}`}>
          <AlarmClock size={10} className="shrink-0" />
          <span className="truncate">{overdue ? 'In ritardo' : 'Oggi'}: {nextTask.text}</span>
        </p>
      )}
    </button>
  );
}
